import { FC, useCallback, useState } from 'react';
import { Button, Form, Input, InputNumber, Modal, Row, Space, Table, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { NAMESPACE, DELETE_ACTION_COLOR } from '@/constants';
import {
  useGetAdminKpisQuery,
  useCreateAdminKpiMutation,
  useUpdateAdminKpiMutation,
  useDeleteAdminKpiMutation,
  useSeedAdminKpisFromRateCardsMutation,
  AdminKpi
} from '@/api';
import { formatPercentInput, parseNumericInput, type NumericInputValue } from '@/utils/inputNumber';

const { confirm } = Modal;

const TraderControlsKpis: FC = () => {
  const { t } = useTranslation(NAMESPACE.COMMON);
  const [form] = Form.useForm<{ kpi: string; benchmark: number }>();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingKpi, setEditingKpi] = useState<AdminKpi | null>(null);

  const { data, isLoading, isError, error } = useGetAdminKpisQuery({ includeInactive: true });
  const [createKpi, { isLoading: isCreating }] = useCreateAdminKpiMutation();
  const [updateKpi, { isLoading: isUpdating }] = useUpdateAdminKpiMutation();
  const [deleteKpi, { isLoading: isDeleting }] = useDeleteAdminKpiMutation();
  const [seedKpisFromRateCards, { isLoading: isSeeding }] = useSeedAdminKpisFromRateCardsMutation();

  const openAddModal = useCallback(() => {
    setEditingKpi(null);
    form.resetFields();
    setModalOpen(true);
  }, [form]);

  const openEditModal = useCallback(
    (record: AdminKpi) => {
      setEditingKpi(record);
      form.setFieldsValue({ kpi: record.kpi, benchmark: record.benchmark });
      setModalOpen(true);
    },
    [form]
  );

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingKpi(null);
    form.resetFields();
  }, [form]);

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      if (editingKpi) {
        await updateKpi({
          id: editingKpi._id,
          body: { kpi: values.kpi, benchmark: values.benchmark }
        }).unwrap();
        message.success(t('kpiHasBeenEdited'));
      } else {
        await createKpi({ kpi: values.kpi, benchmark: values.benchmark }).unwrap();
        message.success(t('kpiHasBeenCreated'));
      }
      closeModal();
    } catch (err) {
      if (err && typeof err === 'object' && 'data' in err) {
        message.error((err as { data?: { message?: string } }).data?.message ?? 'Something went wrong');
      }
    }
  }, [editingKpi, form, createKpi, updateKpi, closeModal, t]);

  const handleSeedFromRateCards = useCallback(() => {
    seedKpisFromRateCards()
      .unwrap()
      .then(() => message.success(t('kpisSeededFromRateCards')))
      .catch(() => message.error(t('kpisSeedFailed')));
  }, [seedKpisFromRateCards, t]);

  const handleDelete = useCallback(
    (record: AdminKpi) => {
      const id = record._id;
      confirm({
        title: <span style={{ color: DELETE_ACTION_COLOR }}>{t('deleteKpiModalTitle')}</span>,
        content: t('deleteKpiModalDescription'),
        icon: <ExclamationCircleOutlined />,
        okText: t('cancel'),
        cancelText: t('delete'),
        cancelButtonProps: { danger: true, style: { color: DELETE_ACTION_COLOR } },
        onCancel: () =>
          deleteKpi(id)
            .unwrap()
            .then(() => message.success(t('kpiHasBeenDeleted')))
      });
    },
    [deleteKpi, t]
  );

  const columns: ColumnsType<AdminKpi> = [
    {
      title: t('kpi'),
      dataIndex: 'kpi',
      key: 'kpi',
      sorter: (a, b) => (a.kpi ?? '').localeCompare(b.kpi ?? ''),
      render: (val) => val ?? '—'
    },
    {
      title: t('benchmark'),
      dataIndex: 'benchmark',
      key: 'benchmark',
      sorter: (a, b) => (a.benchmark ?? 0) - (b.benchmark ?? 0),
      render: (val: number) => (val != null ? `${val}%` : '—')
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" onClick={() => openEditModal(record)}>
            {t('edit')}
          </Button>
          <Button
            type="link"
            size="small"
            danger
            style={{ color: DELETE_ACTION_COLOR }}
            loading={isDeleting}
            onClick={() => handleDelete(record)}
          >
            {t('delete')}
          </Button>
        </Space>
      )
    }
  ];

  const kpis = data?.kpis ?? [];
  const is404 = Boolean(isError && error && 'status' in error && (error as { status: number }).status === 404);

  return (
    <>
      <Typography.Text strong style={{ display: 'block', marginBottom: 16, marginTop: 16, fontSize: 14 }}>
        {t('traderControlsKpis')}
      </Typography.Text>
      <Row style={{ marginBottom: 16 }} gutter={8}>
        <Button type="primary" onClick={openAddModal}>
          {t('addKpi')}
        </Button>
        <Button loading={isSeeding} onClick={handleSeedFromRateCards}>
          {t('seedKpisFromRateCards')}
        </Button>
      </Row>
        {is404 && (
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            {t('kpisApiNotConfigured')}
          </Typography.Text>
        )}
        <Table<AdminKpi>
          columns={columns}
          dataSource={kpis.map((k) => ({ ...k, key: k._id }))}
          loading={isLoading}
          pagination={false}
          locale={{ emptyText: is404 ? t('kpisApiNotConfigured') : t('noData') }}
        />
        <Modal
          title={editingKpi ? t('editKpi') : t('addKpi')}
          open={modalOpen}
          onOk={handleSubmit}
          onCancel={closeModal}
          confirmLoading={isCreating || isUpdating}
          destroyOnHidden
        >
          <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
            <Form.Item
              name="kpi"
              label={t('kpi')}
              rules={[{ required: true, message: t('requiredFieldMessage') }]}
            >
              <Input placeholder={t('kpi')} />
            </Form.Item>
            <Form.Item
              name="benchmark"
              label={t('benchmark')}
              rules={[{ required: true, message: t('requiredFieldMessage') }]}
            >
              <InputNumber<NumericInputValue>
                min={0}
                max={100}
                style={{ width: '100%' }}
                placeholder={t('benchmark')}
                formatter={formatPercentInput}
                parser={parseNumericInput}
              />
            </Form.Item>
          </Form>
        </Modal>
    </>
  );
};

export default TraderControlsKpis;
